import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type AppLanguage = 'es' | 'en' | 'fr' | 'de' | 'pt';

const STORAGE_KEY = 'white-bot:language';

export const languages: { code: AppLanguage; label: string }[] = [
  { code: 'es', label: 'Espanol' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Francais' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Portugues' }
];

const translations = {
  es: {
    'app.account': 'Cuenta',
    'app.admin': 'Admin',
    'app.adminPanel': 'Panel admin',
    'app.buyOrUpgrade': 'Comprar o mejorar',
    'app.configuration': 'Configuracion',
    'app.configurationUnavailable': 'Configuracion no disponible',
    'app.dashboard': 'Panel usuario',
    'app.faq': 'FAQS',
    'app.faqButton': 'Preguntas frecuentes',
    'app.language': 'Idioma',
    'app.license': 'Licencia',
    'app.localAdmin': 'local-admin',
    'app.manageBilling': 'Gestionar suscripcion',
    'app.openAccountMenu': 'Abrir menu de cuenta',
    'app.options': 'Opciones',
    'app.signOut': 'Salir',
    'auth.checkEmail': 'Enlace enviado. Revisa tu correo.',
    'auth.email': 'Email',
    'auth.googleLogin': 'Entrar con Google',
    'auth.googleOpening': 'Abriendo Google...',
    'auth.loadingSession': 'Cargando sesion',
    'auth.subtitle': 'Accede a tu panel para controlar tu slot asignado.',
    'auth.sendLink': 'Enviar enlace',
    'auth.placeholderEmail': 'tu@email.com',
    'dashboard.actions': 'Acciones',
    'dashboard.autoBot': 'Auto Bot',
    'dashboard.collect': 'Recolectar',
    'dashboard.trainTroops': 'Train troops',
    'dashboard.autoHunt': 'Auto Hunt',
    'dashboard.stopBot': 'Parar bot',
    'dashboard.commandSent': 'Comando enviado',
    'dashboard.noPendingActions': 'Sin acciones pendientes',
    'dashboard.readOnly': 'Modo solo lectura. Estado, colas y captura se pueden consultar; los comandos reales estan bloqueados.',
    'dashboard.bridgeUnavailable': 'Bridge no disponible. Esperando heartbeat reciente del agente.',
    'dashboard.servicePreparing': 'Estamos preparando tu bot. Intentalo de nuevo en unos segundos.',
    'dashboard.commandsReadError': 'No se pudo leer la cola de comandos. Revisa permisos Supabase.',
    'dashboard.waitingStatus': 'Esperando estado del bot',
    'dashboard.screenshot': 'Captura',
    'dashboard.colony': 'Colonia',
    'dashboard.world': 'Mundo',
    'dashboard.noRecentVerification': 'Sin verificacion reciente',
    'dashboard.mockView': 'Vista mock - sin conexion real al emulador',
    'dashboard.slotNotOperational': 'Slot no operativo',
    'dashboard.provisioningReview': 'Provisioning requiere revision',
    'dashboard.options': 'Opciones',
    'dashboard.reconnect': 'Reconexion',
    'dashboard.reconnectNow': 'Reconectar ahora',
    'dashboard.reconnectAuto': 'Reconectar auto',
    'dashboard.reconnectDelay': 'Tiempo entre intentos',
    'dashboard.reconnectSaved': 'Ajustes de reconexion guardados',
    'dashboard.reconnectRequested': 'Reconexion solicitada',
    'dashboard.reconnecting': 'Reconectando...',
    'dashboard.accountOtherDevice': 'Cuenta abierta en otro dispositivo',
    'dashboard.mobileLoginBlocker': 'La cuenta inicio sesion en otro dispositivo. Reconecta para continuar.',
    'dashboard.reconnectWatch': 'Vigilancia de reconexion activa',
    'dashboard.reconnectPause': 'El bot pausa los clicks cuando detecta una sesion abierta en otro dispositivo.',
    'reconnect.eyebrow': 'Reconexion',
    'reconnect.workingTitle': 'Reconectando tu cuenta',
    'reconnect.workingBody': 'Estamos volviendo a abrir tu cuenta en el servidor. Esto puede tardar unos segundos; no cierres esta ventana.',
    'reconnect.slowNote': 'Esta tardando un poco mas de lo normal. Puedes cerrar esta ventana: la reconexion seguira en segundo plano.',
    'reconnect.successTitle': '¡Cuenta reconectada!',
    'reconnect.successBody': 'Tu cuenta volvio a estar lista. Ya puedes seguir usando el bot con normalidad.',
    'reconnect.errorTitle': 'No pudimos reconectar',
    'reconnect.errorBody': 'No se pudo reconectar la cuenta. Espera unos segundos y vuelve a intentarlo.',
    'reconnect.busyTitle': 'El bot esta ocupado',
    'reconnect.busyBody': 'Hay otra accion en curso. Deten el bot y vuelve a intentar la reconexion.',
    'reconnect.timeoutTitle': 'Esta tardando mas de lo normal',
    'reconnect.timeoutBody': 'La reconexion no ha confirmado a tiempo. Comprueba la captura del juego o vuelve a intentarlo.',
    'reconnect.retry': 'Reintentar',
    'reconnect.close': 'Cerrar',
    'operation.status': 'Estado',
    'operation.time': 'Tiempo',
    'operation.close': 'Cerrar',
    'actionModal.eyebrow': 'Accion del bot',
    'actionModal.sendingTitle': 'Enviando {action}',
    'actionModal.sendingBody': 'Estamos enviando la accion al servidor. Espera la confirmacion antes de tocar otro boton.',
    'actionModal.queuedTitle': '{action} en cola',
    'actionModal.queuedBody': 'El comando esta en cola para tu worker. En cuanto lo recoja veremos el siguiente estado.',
    'actionModal.deliveredTitle': 'Preparando {action}',
    'actionModal.deliveredBody': 'El worker ya recibio el comando y esta preparando el emulador.',
    'actionModal.workingTitle': '{action} en marcha',
    'actionModal.workingBody': 'El bot acepto la accion y ya esta trabajando. Puedes cerrar esta ventana cuando termine la confirmacion.',
    'actionModal.stopRunningTitle': 'Parando el bot',
    'actionModal.stopRunningBody': 'Estamos parando la accion actual de forma ordenada para dejar tu slot listo.',
    'actionModal.successTitle': '{action} iniciada',
    'actionModal.successBody': 'El bot confirmo la accion y el panel queda listo para la siguiente decision.',
    'actionModal.stopSuccessTitle': 'Bot parado',
    'actionModal.stopSuccessBody': 'El bot se ha detenido correctamente. Ya puedes lanzar otra accion cuando quieras.',
    'actionModal.stopBlockedSuccessBody': 'El bot se ha detenido. Cuando termines en el movil, usa Reconectar ahora para continuar.',
    'actionModal.errorTitle': 'No se pudo completar {action}',
    'actionModal.errorBody': 'La accion no se pudo confirmar. Revisa el estado del bot y vuelve a intentarlo en unos segundos.',
    'actionModal.blockedBody': 'La cuenta necesita reconexion antes de continuar. El bot no seguira haciendo clicks hasta que lo reconectes.',
    'actionModal.timeoutTitle': '{action} tarda demasiado',
    'actionModal.timeoutBody': 'No hemos recibido una confirmacion final a tiempo. Revisa la captura o espera a que el estado del bot se actualice.',
    'actionModal.statusSending': 'enviando',
    'actionModal.statusQueued': 'en cola',
    'actionModal.statusDelivered': 'entregado',
    'actionModal.statusRunning': 'trabajando',
    'actionModal.statusSuccess': 'listo',
    'actionModal.statusError': 'error',
    'actionModal.statusTimeout': 'sin confirmacion',
    'activity.ready': 'Bot listo',
    'activity.sending': 'Enviando comando',
    'activity.contactingServer': 'contactando servidor',
    'activity.commandQueued': 'Comando en cola',
    'activity.commandDelivered': 'Worker preparando accion',
    'activity.waitingWorker': 'esperando confirmacion del worker',
    'activity.waitingConfirmation': 'Esperando confirmacion',
    'activity.refreshingStatus': 'actualizando estado',
    'activity.buttonsDelayed': 'Los botones pueden tardar unos segundos en reflejar el estado real.',
    'activity.starting': 'Preparando bot',
    'activity.autoBotRunning': 'Auto Bot en marcha',
    'activity.training': 'Entrenando tropas',
    'activity.hunting': 'Buscando objetivos',
    'activity.working': 'Bot trabajando',
    'activity.stopping': 'Parando bot',
    'activity.reconnecting': 'Reconectando cuenta',
    'activity.blocked': 'Accion bloqueada',
    'activity.blockedReason': 'El bot necesita atencion antes de continuar.',
    'activity.workerRunning': 'El worker esta ejecutando una accion.',
    'activity.waitingNextCheck': 'Esperando siguiente comprobacion',
    'activity.bridgeOffline': 'Bridge desconectado',
    'activity.servicePreparing': 'Preparando servicio',
    'intelligence.title': 'Intelligence',
    'intelligence.region': 'Region',
    'intelligence.regionPlaceholder': 'Region',
    'intelligence.searchPlayer': 'Buscar jugador o alianza',
    'intelligence.searchPlaceholder': 'Jugador o alianza',
    'intelligence.request': 'Solicitar',
    'intelligence.requesting': 'Solicitando...',
    'intelligence.minChars': 'Escribe al menos 3 caracteres.',
    'intelligence.requestQueued': 'Solicitud de inteligencia enviada',
    'intelligence.loading': 'Cargando solicitudes',
    'intelligence.empty': 'No hay solicitudes todavia',
    'intelligence.player': 'Jugador',
    'intelligence.search': 'Busqueda',
    'intelligence.status': 'Estado',
    'intelligence.date': 'Fecha',
    'intelligence.result': 'Resultado',
    'intelligence.statusPending': 'En cola',
    'intelligence.statusProcessing': 'Buscando',
    'intelligence.statusCompleted': 'Completada',
    'intelligence.statusFailed': 'No se pudo obtener el resultado',
    'intelligence.statusNotFound': 'No encontrado',
    'intelligence.notFound': 'Sin coincidencias',
    'intelligence.noResult': 'Resultado pendiente',
    'intelligence.power': 'Poder',
    'intelligence.coordinates': 'Coordenadas',
    'intelligence.lastSeen': 'Visto',
    'intelligence.resultsFound': '{count} resultados',
    'intelligence.showMore': 'Ver {count} mas',
    'intelligence.showLess': 'Ver menos',
    'intelligence.sortResults': 'Ordenar resultados',
    'intelligence.sortPower': 'Poder',
    'intelligence.sortName': 'Nombre',
    'intelligence.guarantee': 'Intelligence beta usa detecciones regionales cacheadas. Es una estimacion, no una garantia de ubicacion en tiempo real.',
    'intelligence.quota': 'Trial: 10 busquedas/dia. Pro: 1 busqueda/dia. Pro+: 10 busquedas/dia.',
    'intelligence.fresh': 'cache reciente',
    'intelligence.stale': 'cache antiguo',
    'intelligence.confidence': 'Confianza',
    'intelligence.quotaRemaining': 'Busquedas disponibles hoy',
    'intelligence.quotaRemainingValue': '{remaining} de {limit}',
    'intelligence.quotaKicker': 'Limite diario alcanzado',
    'intelligence.quotaTitle': 'No te quedan busquedas de Intelligence hoy',
    'intelligence.quotaBody': 'Tu cuota se reinicia cada dia en horario UTC. Puedes volver manana o revisar un plan con mas busquedas diarias.',
    'intelligence.quotaTrialLabel': 'Trial',
    'intelligence.quotaTrialValue': '10 busquedas/dia',
    'intelligence.quotaProLabel': 'Pro',
    'intelligence.quotaProValue': '1 busqueda/dia',
    'intelligence.quotaProPlusLabel': 'Pro+',
    'intelligence.quotaProPlusValue': '10 busquedas/dia',
    'intelligence.quotaUpgrade': 'Ver upgrade',
    'intelligence.quotaClose': 'Cerrar',
    'intelligence.delete': 'Eliminar busqueda',
    'intelligence.deleteConfirm': 'Eliminar "{name}" del panel? La busqueda seguira contando para tu limite diario.',
    'intelligence.deleteSuccess': 'Busqueda de Intelligence eliminada del panel',
    'tutorial.title': 'Primeros pasos',
    'tutorial.intro': 'Antes de lanzar acciones, revisa este flujo rapido para dejar tu bot funcionando sin soporte manual.',
    'tutorial.step1.title': '1. Vincula tu cuenta',
    'tutorial.step1.body': 'Inicia la vinculacion y entra con Google dentro del emulador asignado. El bot no guarda tu contrasena.',
    'tutorial.step2.title': '2. Espera el estado listo',
    'tutorial.step2.body': 'Cuando licencia, vinculacion, bridge y provisioning esten en verde, los botones de acciones se activaran.',
    'tutorial.step3.title': '3. Configura y ejecuta',
    'tutorial.step3.body': 'Ajusta recolectar, entrenamiento o caza automatica. Auto Bot encadena los modulos seleccionados.',
    'tutorial.doNotShow': 'No volver a mostrar',
    'tutorial.later': 'Ahora no',
    'tutorial.start': 'Entendido',
    'linking.title': 'Vincular cuenta',
    'linking.activeSession': 'Sesion activa',
    'linking.pendingAccount': 'Cuenta pendiente',
    'linking.start': 'Iniciar vinculacion',
    'linking.started': 'Sesion de vinculacion iniciada',
    'linking.completed': 'Cuenta vinculada',
    'linking.cancelled': 'Vinculacion cancelada',
    'linking.claimingSlotTitle': 'Buscando slot disponible',
    'linking.claimingSlotBody': 'Estamos reservando automaticamente un servidor con un slot preparado para tu cuenta.',
    'linking.claimingSlotButton': 'Buscando slot...',
    'linking.noCapacityTitle': 'Sin slots disponibles ahora',
    'linking.noCapacityBody': 'Tu cuenta esta en espera. En cuanto haya un servidor con plaza preparada podras iniciar la vinculacion.',
    'linking.retryCapacity': 'Buscar slot disponible',
    'linking.claimSlotProblemTitle': 'No se pudo reservar slot',
    'linking.claimSlotProblemBody': 'Ha habido un problema al comprobar la capacidad. Puedes reintentarlo o esperar a que soporte lo revise.',
    'linking.slotClaimed': 'Slot reservado. Ya puedes iniciar la vinculacion.',
    'linking.activeMessage': 'Paso 1 de 2: escribe en el cuadro de abajo el email de Google de tu cuenta del juego y pulsa Enviar.',
    'linking.passwordMessage': 'Paso 2 de 2: escribe en el cuadro de abajo la contrasena de Google y pulsa Enviar. Despues la vinculacion se completa sola.',
    'linking.actionsBlockedNotice': 'Vinculacion en curso. Termina o cancela la vinculacion para poder usar el bot.',
    'linking.actionsBlockedTitle': 'Termina la vinculacion primero',
    'linking.preparingMessage': 'Estamos preparando el emulador. Podras introducir tu cuenta cuando aparezca Google.',
    'linking.startMessage': 'Inicia una sesion temporal para entrar en Google Play desde tu emulador asignado.',
    'linking.textToSend': 'Texto para enviar al emulador',
    'linking.placeholder': 'email, nombre o codigo',
    'linking.placeholderPassword': 'contrasena de Google',
    'linking.send': 'Enviar',
    'linking.showText': 'Mostrar texto',
    'linking.hideText': 'Ocultar texto',
    'linking.back': 'Atras',
    'linking.complete': 'Login completado',
    'linking.cancel': 'Cancelar',
    'linking.expiresIn': 'Expira en {time}',
    'linking.inputQueued': 'Entrada enviada. Actualizando captura...',
    'linking.captureRequired': 'Espera a que aparezca una captura real del emulador antes de completar.',
    'linking.waitStartingTitle': 'Preparando vinculacion',
    'linking.waitStartingBody': 'Estamos abriendo una sesion temporal segura en tu emulador. Puede tardar unos segundos.',
    'linking.prepareTitle': 'Preparando vinculacion',
    'linking.prepareWorkerBody': 'Estamos arrancando el emulador asignado y conectando con el servidor local.',
    'linking.prepareCaptureBody': 'Estamos esperando una captura estable del emulador antes de continuar.',
    'linking.prepareNavigatingBody': 'Estamos preparando la pantalla segura de inicio de sesion.',
    'linking.prepareOpeningGoogleBody': 'Estamos abriendo Google Play y enfocando el campo de correo. Enseguida podras introducir tu cuenta.',
    'linking.prepareFailedTitle': 'No se pudo preparar la vinculacion',
    'linking.prepareFailedBody': 'No hemos llegado a la pantalla de Google a tiempo. Cancela la sesion y vuelve a intentarlo; soporte podra revisar el ultimo paso registrado.',
    'linking.notReadyTitle': 'El emulador aun se esta preparando',
    'linking.notReadyBody': 'Estamos arrancando tu emulador y conectando con el servidor. Espera a que este listo antes de iniciar la vinculacion; en unos segundos podras empezar.',
    'linking.inputStalledTitle': 'No detectamos cambios en la pantalla',
    'linking.inputStalledBody': 'Tu entrada se envio pero la pantalla del emulador no ha cambiado. Reintenta tocar o escribir, o cancela la vinculacion si el problema continua.',
    'linking.inputStalledRetry': 'Reintentar',
    'linking.prepareStatusClaiming': 'reservando',
    'linking.prepareStatusStarting': 'iniciando',
    'linking.prepareStatusWorker': 'arrancando',
    'linking.prepareStatusCapture': 'capturando',
    'linking.prepareStatusNavigating': 'navegando',
    'linking.prepareStatusOpeningGoogle': 'abriendo google play',
    'linking.prepareStatusFailed': 'requiere revision',
    'linking.prepareStatusCompleting': 'confirmando',
    'linking.prepareStatusCancelling': 'cancelando',
    'linking.waitInputTitle': 'Entrada enviada',
    'linking.waitInputBody': 'Estamos actualizando la captura del emulador. Si ya tocaste o escribiste, espera a ver el cambio antes de repetir.',
    'linking.waitPasswordInputBody': 'Estamos enviando la contrasena al emulador. Espera a que aparezca la siguiente pantalla antes de tocar nada.',
    'linking.waitEmailTitle': 'Correo enviado',
    'linking.waitEmailBody': 'El correo se ha enviado al emulador. Estamos pulsando Siguiente y esperando la pantalla de contrasena.',
    'linking.waitPasswordTitle': 'Finalizando vinculacion',
    'linking.waitPasswordBody': 'La contrasena se ha enviado. Estamos aceptando las pantallas conocidas de Google y configurando la cuenta.',
    'linking.recoveryMissingTitle': 'Faltan datos de recuperacion',
    'linking.recoveryMissingBody': 'Google pide telefono y correo de recuperacion. Completa esos datos en la cuenta o avisa a soporte antes de continuar.',
    'linking.waitCompleteTitle': 'Confirmando cuenta',
    'linking.waitCompleteBody': 'Estamos cerrando la sesion temporal y marcando el slot como vinculado. No cierres esta pantalla.',
    'linking.waitCancelTitle': 'Cancelando vinculacion',
    'linking.waitCancelBody': 'Estamos deteniendo la sesion temporal para que el slot no quede asociado a una cuenta incorrecta.',
    'linking.waitOverlay': 'Actualizando captura...',
    'linking.waitOverlayHint': 'Puede tardar unos segundos. No repitas el tap si ya se envio.',
    'linking.slowDownTitle': 'Demasiadas entradas seguidas',
    'linking.slowDownBody': 'Hemos pausado un instante los taps o teclas para proteger la sesion. Espera unos segundos y continua normalmente.',
    'linking.expiredTitle': 'Sesion expirada',
    'linking.expiredBody': 'La sesion temporal ya no acepta entradas. Inicia una nueva vinculacion para continuar en el mismo slot.',
    'linking.wrongAccount': 'Cuenta incorrecta',
    'linking.wrongAccountTitle': 'Vinculacion reiniciada',
    'linking.wrongAccountBody': 'La sesion se cancelo para evitar completar el slot con una cuenta equivocada. Vuelve a iniciar la vinculacion y entra con la cuenta correcta. Soporte vera el evento auditado.',
    'linking.wrongAccountCancelled': 'Vinculacion cancelada por cuenta incorrecta',
    'linking.assistance': 'Solicitar asistencia',
    'linking.assistanceRequested': 'Asistencia solicitada. Un operador se unira al chat en cuanto pueda.',
    'linking.assistancePending': 'Asistencia solicitada',
    'linking.chatTitle': 'Asistencia',
    'linking.chatPlaceholder': 'Escribe tu mensaje para soporte',
    'linking.chatSend': 'Enviar mensaje',
    'linking.chatEmpty': 'Cuentanos que problema tienes y un operador te ayudara a terminar la vinculacion.',
    'linking.senderOperator': 'Operador',
    'linking.senderYou': 'Tu',
    'linking.operatorActiveTitle': 'Un operador esta ayudando',
    'linking.operatorActiveBody': 'Un operador esta completando tu vinculacion. No toques la pantalla; te avisara por el chat cuando termine.',
    'settings.resources.iron': 'Hierro',
    'settings.resources.coal': 'Carbon',
    'settings.resources.wood': 'Madera',
    'settings.resources.meat': 'Carne',
    'settings.units.infantry': 'Infanteria',
    'settings.units.lancer': 'Lanceros',
    'settings.units.marksman': 'Tiradores',
    'settings.level': 'Nivel',
    'settings.marches': 'Marchas',
    'settings.amount': 'Cantidad',
    'settings.type': 'Tipo',
    'settings.beasts': 'Bestias',
    'settings.terror': 'Terror',
    'settings.timer': 'Temporizador',
    'settings.down': 'Bajar',
    'settings.up': 'Subir'
  },
  en: {
    'app.account': 'Account',
    'app.admin': 'Admin',
    'app.adminPanel': 'Admin panel',
    'app.buyOrUpgrade': 'Buy or upgrade',
    'app.configuration': 'Configuration',
    'app.configurationUnavailable': 'Configuration unavailable',
    'app.dashboard': 'User panel',
    'app.faq': 'FAQ',
    'app.faqButton': 'Frequently asked questions',
    'app.language': 'Language',
    'app.license': 'License',
    'app.localAdmin': 'local-admin',
    'app.manageBilling': 'Manage subscription',
    'app.openAccountMenu': 'Open account menu',
    'app.options': 'Options',
    'app.signOut': 'Sign out',
    'auth.checkEmail': 'Link sent. Check your email.',
    'auth.email': 'Email',
    'auth.googleLogin': 'Continue with Google',
    'auth.googleOpening': 'Opening Google...',
    'auth.loadingSession': 'Loading session',
    'auth.subtitle': 'Access your panel to control your assigned bot slot.',
    'auth.sendLink': 'Send link',
    'auth.placeholderEmail': 'you@email.com',
    'dashboard.actions': 'Actions',
    'dashboard.autoBot': 'Auto Bot',
    'dashboard.collect': 'Collect',
    'dashboard.trainTroops': 'Train troops',
    'dashboard.autoHunt': 'Auto Hunt',
    'dashboard.stopBot': 'Stop bot',
    'dashboard.commandSent': 'Command sent',
    'dashboard.noPendingActions': 'No pending actions',
    'dashboard.readOnly': 'Read-only mode. Status, queues and screenshot are visible; real commands are blocked.',
    'dashboard.bridgeUnavailable': 'Bridge unavailable. Waiting for a recent agent heartbeat.',
    'dashboard.servicePreparing': 'We are preparing your bot. Try again in a few seconds.',
    'dashboard.commandsReadError': 'Could not read command queue. Check Supabase permissions.',
    'dashboard.waitingStatus': 'Waiting for bot status',
    'dashboard.screenshot': 'Screenshot',
    'dashboard.colony': 'Colony',
    'dashboard.world': 'World',
    'dashboard.noRecentVerification': 'No recent verification',
    'dashboard.mockView': 'Mock view - no real emulator connection',
    'dashboard.slotNotOperational': 'Slot not operational',
    'dashboard.provisioningReview': 'Provisioning needs review',
    'dashboard.options': 'Options',
    'dashboard.reconnect': 'Reconnect',
    'dashboard.reconnectNow': 'Reconnect now',
    'dashboard.reconnectAuto': 'Auto reconnect',
    'dashboard.reconnectDelay': 'Time between attempts',
    'dashboard.reconnectSaved': 'Reconnect settings saved',
    'dashboard.reconnectRequested': 'Reconnect requested',
    'dashboard.reconnecting': 'Reconnecting...',
    'dashboard.accountOtherDevice': 'Account open on another device',
    'dashboard.mobileLoginBlocker': 'The account signed in on another device. Reconnect to continue.',
    'dashboard.reconnectWatch': 'Reconnect watcher active',
    'dashboard.reconnectPause': 'The bot pauses clicks when it detects a session open on another device.',
    'reconnect.eyebrow': 'Reconnection',
    'reconnect.workingTitle': 'Reconnecting your account',
    'reconnect.workingBody': 'We are reopening your account on the server. This can take a few seconds; please keep this window open.',
    'reconnect.slowNote': 'This is taking a little longer than usual. You can close this window: the reconnection will keep running in the background.',
    'reconnect.successTitle': 'Account reconnected!',
    'reconnect.successBody': 'Your account is ready again. You can keep using the bot as usual.',
    'reconnect.errorTitle': "We couldn't reconnect",
    'reconnect.errorBody': 'The account could not be reconnected. Wait a few seconds and try again.',
    'reconnect.busyTitle': 'The bot is busy',
    'reconnect.busyBody': 'Another action is running. Stop the bot and try reconnecting again.',
    'reconnect.timeoutTitle': 'This is taking longer than usual',
    'reconnect.timeoutBody': "The reconnection hasn't confirmed in time. Check the game screenshot or try again.",
    'reconnect.retry': 'Try again',
    'reconnect.close': 'Close',
    'operation.status': 'Status',
    'operation.time': 'Time',
    'operation.close': 'Close',
    'actionModal.eyebrow': 'Bot action',
    'actionModal.sendingTitle': 'Sending {action}',
    'actionModal.sendingBody': 'We are sending the action to the server. Wait for confirmation before pressing another button.',
    'actionModal.queuedTitle': '{action} queued',
    'actionModal.queuedBody': 'The command is queued for your worker. As soon as it picks it up we will show the next state.',
    'actionModal.deliveredTitle': 'Preparing {action}',
    'actionModal.deliveredBody': 'The worker received the command and is preparing the emulator.',
    'actionModal.workingTitle': '{action} running',
    'actionModal.workingBody': 'The bot accepted the action and is now working. You can close this window after confirmation.',
    'actionModal.stopRunningTitle': 'Stopping the bot',
    'actionModal.stopRunningBody': 'We are stopping the current action cleanly so your slot is ready.',
    'actionModal.successTitle': '{action} started',
    'actionModal.successBody': 'The bot confirmed the action and the panel is ready for the next decision.',
    'actionModal.stopSuccessTitle': 'Bot stopped',
    'actionModal.stopSuccessBody': 'The bot stopped correctly. You can start another action whenever you are ready.',
    'actionModal.stopBlockedSuccessBody': 'The bot stopped. When you are done on mobile, use Reconnect now to continue.',
    'actionModal.errorTitle': "Couldn't complete {action}",
    'actionModal.errorBody': 'The action could not be confirmed. Check the bot status and try again in a few seconds.',
    'actionModal.blockedBody': 'The account needs reconnection before continuing. The bot will not keep clicking until you reconnect.',
    'actionModal.timeoutTitle': '{action} is taking too long',
    'actionModal.timeoutBody': 'We did not receive final confirmation in time. Check the screenshot or wait for the bot status to refresh.',
    'actionModal.statusSending': 'sending',
    'actionModal.statusQueued': 'queued',
    'actionModal.statusDelivered': 'delivered',
    'actionModal.statusRunning': 'working',
    'actionModal.statusSuccess': 'ready',
    'actionModal.statusError': 'error',
    'actionModal.statusTimeout': 'no confirmation',
    'activity.ready': 'Bot ready',
    'activity.sending': 'Sending command',
    'activity.contactingServer': 'contacting server',
    'activity.commandQueued': 'Command queued',
    'activity.commandDelivered': 'Worker preparing action',
    'activity.waitingWorker': 'waiting for worker confirmation',
    'activity.waitingConfirmation': 'Waiting for confirmation',
    'activity.refreshingStatus': 'refreshing status',
    'activity.buttonsDelayed': 'Buttons can take a few seconds to reflect the real state.',
    'activity.starting': 'Preparing bot',
    'activity.autoBotRunning': 'Auto Bot running',
    'activity.training': 'Training troops',
    'activity.hunting': 'Searching targets',
    'activity.working': 'Bot working',
    'activity.stopping': 'Stopping bot',
    'activity.reconnecting': 'Reconnecting account',
    'activity.blocked': 'Action blocked',
    'activity.blockedReason': 'The bot needs attention before continuing.',
    'activity.workerRunning': 'The worker is executing an action.',
    'activity.waitingNextCheck': 'Waiting for next check',
    'activity.bridgeOffline': 'Bridge disconnected',
    'activity.servicePreparing': 'Preparing service',
    'intelligence.title': 'Intelligence',
    'intelligence.region': 'Region',
    'intelligence.regionPlaceholder': 'Region',
    'intelligence.searchPlayer': 'Search player or alliance',
    'intelligence.searchPlaceholder': 'Player or alliance',
    'intelligence.request': 'Request',
    'intelligence.requesting': 'Requesting...',
    'intelligence.minChars': 'Enter at least 3 characters.',
    'intelligence.requestQueued': 'Intelligence request sent',
    'intelligence.loading': 'Loading requests',
    'intelligence.empty': 'No requests yet',
    'intelligence.player': 'Player',
    'intelligence.search': 'Search',
    'intelligence.status': 'Status',
    'intelligence.date': 'Date',
    'intelligence.result': 'Result',
    'intelligence.statusPending': 'Queued',
    'intelligence.statusProcessing': 'Searching',
    'intelligence.statusCompleted': 'Completed',
    'intelligence.statusFailed': 'Could not get the result',
    'intelligence.statusNotFound': 'Not found',
    'intelligence.notFound': 'No matches found',
    'intelligence.noResult': 'Result pending',
    'intelligence.power': 'Power',
    'intelligence.coordinates': 'Coordinates',
    'intelligence.lastSeen': 'Seen',
    'intelligence.resultsFound': '{count} results',
    'intelligence.showMore': 'Show {count} more',
    'intelligence.showLess': 'Show less',
    'intelligence.sortResults': 'Sort results',
    'intelligence.sortPower': 'Power',
    'intelligence.sortName': 'Name',
    'intelligence.guarantee': 'Beta Intelligence uses cached regional detections. It is an estimate, not a real-time location guarantee.',
    'intelligence.quota': 'Trial: 10 searches/day. Pro: 1 search/day. Pro+: 10 searches/day.',
    'intelligence.fresh': 'fresh cache',
    'intelligence.stale': 'old cache',
    'intelligence.confidence': 'Confidence',
    'intelligence.quotaRemaining': 'Searches left today',
    'intelligence.quotaRemainingValue': '{remaining} of {limit}',
    'intelligence.quotaKicker': 'Daily limit reached',
    'intelligence.quotaTitle': 'You have no Intelligence searches left today',
    'intelligence.quotaBody': 'Your quota resets every day on UTC time. You can come back tomorrow or review a plan with more daily searches.',
    'intelligence.quotaTrialLabel': 'Trial',
    'intelligence.quotaTrialValue': '10 searches/day',
    'intelligence.quotaProLabel': 'Pro',
    'intelligence.quotaProValue': '1 search/day',
    'intelligence.quotaProPlusLabel': 'Pro+',
    'intelligence.quotaProPlusValue': '10 searches/day',
    'intelligence.quotaUpgrade': 'View upgrade',
    'intelligence.quotaClose': 'Close',
    'intelligence.delete': 'Delete search',
    'intelligence.deleteConfirm': 'Delete "{name}" from the panel? This search will still count toward your daily limit.',
    'intelligence.deleteSuccess': 'Intelligence search removed from the panel',
    'tutorial.title': 'First steps',
    'tutorial.intro': 'Before launching actions, review this quick flow to get your bot running without manual support.',
    'tutorial.step1.title': '1. Link your account',
    'tutorial.step1.body': 'Start linking and sign in with Google inside your assigned emulator. The bot does not store your password.',
    'tutorial.step2.title': '2. Wait for ready status',
    'tutorial.step2.body': 'When license, linking, bridge and provisioning are green, the action buttons become available.',
    'tutorial.step3.title': '3. Configure and run',
    'tutorial.step3.body': 'Adjust collecting, training or auto hunt. Auto Bot chains the selected modules.',
    'tutorial.doNotShow': 'Do not show again',
    'tutorial.later': 'Not now',
    'tutorial.start': 'Got it',
    'linking.title': 'Link account',
    'linking.activeSession': 'Active session',
    'linking.pendingAccount': 'Pending account',
    'linking.start': 'Start linking',
    'linking.started': 'Linking session started',
    'linking.completed': 'Account linked',
    'linking.cancelled': 'Linking cancelled',
    'linking.claimingSlotTitle': 'Finding an available slot',
    'linking.claimingSlotBody': 'We are automatically reserving a prepared server slot for your account.',
    'linking.claimingSlotButton': 'Finding slot...',
    'linking.noCapacityTitle': 'No slots available right now',
    'linking.noCapacityBody': 'Your account is waiting. As soon as a prepared server slot is available, you will be able to start linking.',
    'linking.retryCapacity': 'Find available slot',
    'linking.claimSlotProblemTitle': 'Could not reserve a slot',
    'linking.claimSlotProblemBody': 'There was a problem checking capacity. You can retry or wait for support to review it.',
    'linking.slotClaimed': 'Slot reserved. You can start linking now.',
    'linking.activeMessage': 'Step 1 of 2: type the Google email of your game account in the box below and press Send.',
    'linking.passwordMessage': 'Step 2 of 2: enter your Google password in the box below and press Send. Linking then completes automatically.',
    'linking.actionsBlockedNotice': 'Linking in progress. Finish or cancel linking before you can use the bot.',
    'linking.actionsBlockedTitle': 'Finish linking first',
    'linking.preparingMessage': 'We are preparing the emulator. You can enter your account when Google appears.',
    'linking.startMessage': 'Start a temporary session to sign in to Google Play in your assigned emulator.',
    'linking.textToSend': 'Text to send to emulator',
    'linking.placeholder': 'email, name or code',
    'linking.placeholderPassword': 'Google password',
    'linking.send': 'Send',
    'linking.showText': 'Show text',
    'linking.hideText': 'Hide text',
    'linking.back': 'Back',
    'linking.complete': 'Login completed',
    'linking.cancel': 'Cancel',
    'linking.expiresIn': 'Expires in {time}',
    'linking.inputQueued': 'Input sent. Updating screenshot...',
    'linking.captureRequired': 'Wait for a real emulator screenshot before completing.',
    'linking.waitStartingTitle': 'Preparing linking',
    'linking.waitStartingBody': 'We are opening a secure temporary session in your emulator. This can take a few seconds.',
    'linking.prepareTitle': 'Preparing linking',
    'linking.prepareWorkerBody': 'We are starting the assigned emulator and connecting to the local server.',
    'linking.prepareCaptureBody': 'We are waiting for a stable emulator capture before continuing.',
    'linking.prepareNavigatingBody': 'We are preparing the secure sign-in screen.',
    'linking.prepareOpeningGoogleBody': 'We are opening Google Play and focusing the email field. You will be able to enter your account in a moment.',
    'linking.prepareFailedTitle': 'Could not prepare linking',
    'linking.prepareFailedBody': 'We did not reach the Google screen in time. Cancel this session and try again; support can review the last recorded step.',
    'linking.notReadyTitle': 'The emulator is still getting ready',
    'linking.notReadyBody': 'We are starting your emulator and connecting to the server. Wait until it is ready before starting linking; you will be able to begin in a few seconds.',
    'linking.inputStalledTitle': 'No screen change detected',
    'linking.inputStalledBody': 'Your input was sent but the emulator screen has not changed. Try tapping or typing again, or cancel linking if the problem continues.',
    'linking.inputStalledRetry': 'Try again',
    'linking.prepareStatusClaiming': 'reserving',
    'linking.prepareStatusStarting': 'starting',
    'linking.prepareStatusWorker': 'starting',
    'linking.prepareStatusCapture': 'capturing',
    'linking.prepareStatusNavigating': 'navigating',
    'linking.prepareStatusOpeningGoogle': 'opening google play',
    'linking.prepareStatusFailed': 'needs review',
    'linking.prepareStatusCompleting': 'confirming',
    'linking.prepareStatusCancelling': 'cancelling',
    'linking.waitInputTitle': 'Input sent',
    'linking.waitInputBody': 'We are updating the emulator screenshot. If you already tapped or typed, wait for the screen to change before repeating it.',
    'linking.waitPasswordInputBody': 'We are sending the password to the emulator. Wait for the next screen before touching anything.',
    'linking.waitEmailTitle': 'Email sent',
    'linking.waitEmailBody': 'The email was sent to the emulator. We are pressing Next and waiting for the password screen.',
    'linking.waitPasswordTitle': 'Finishing linking',
    'linking.waitPasswordBody': 'The password was sent. We are accepting the known Google screens and configuring the account.',
    'linking.recoveryMissingTitle': 'Recovery details missing',
    'linking.recoveryMissingBody': 'Google is asking for recovery phone and email details. Complete those details on the account or contact support before continuing.',
    'linking.waitCompleteTitle': 'Confirming account',
    'linking.waitCompleteBody': 'We are closing the temporary session and marking the slot as linked. Keep this screen open.',
    'linking.waitCancelTitle': 'Cancelling linking',
    'linking.waitCancelBody': 'We are stopping the temporary session so the slot is not associated with the wrong account.',
    'linking.waitOverlay': 'Updating screenshot...',
    'linking.waitOverlayHint': 'This can take a few seconds. Do not repeat the tap if it was already sent.',
    'linking.slowDownTitle': 'Too many inputs in a row',
    'linking.slowDownBody': 'Inputs are paused for a moment to protect the session. Wait a few seconds and continue normally.',
    'linking.expiredTitle': 'Session expired',
    'linking.expiredBody': 'The temporary session no longer accepts input. Start a new linking session to continue in the same slot.',
    'linking.wrongAccount': 'Wrong account',
    'linking.wrongAccountTitle': 'Linking restarted',
    'linking.wrongAccountBody': 'The session was cancelled so the slot is not completed with the wrong account. Start linking again and sign in with the correct account. Support will see the audited event.',
    'linking.wrongAccountCancelled': 'Linking cancelled for wrong account',
    'linking.assistance': 'Request assistance',
    'linking.assistanceRequested': 'Assistance requested. An operator will join the chat as soon as possible.',
    'linking.assistancePending': 'Assistance requested',
    'linking.chatTitle': 'Assistance',
    'linking.chatPlaceholder': 'Type your message to support',
    'linking.chatSend': 'Send message',
    'linking.chatEmpty': 'Tell us what is going wrong and an operator will help you finish linking.',
    'linking.senderOperator': 'Operator',
    'linking.senderYou': 'You',
    'linking.operatorActiveTitle': 'An operator is helping',
    'linking.operatorActiveBody': 'An operator is completing your linking. Do not touch the screen; they will message you in the chat when done.',
    'settings.resources.iron': 'Iron',
    'settings.resources.coal': 'Coal',
    'settings.resources.wood': 'Wood',
    'settings.resources.meat': 'Meat',
    'settings.units.infantry': 'Infantry',
    'settings.units.lancer': 'Lancers',
    'settings.units.marksman': 'Marksmen',
    'settings.level': 'Level',
    'settings.marches': 'Marches',
    'settings.amount': 'Amount',
    'settings.type': 'Type',
    'settings.beasts': 'Beasts',
    'settings.terror': 'Terror',
    'settings.timer': 'Timer',
    'settings.down': 'Decrease',
    'settings.up': 'Increase'
  },
  fr: {},
  de: {},
  pt: {}
} as const;

export type TranslationKey = keyof typeof translations.es;

const fallbackByLanguage: Record<AppLanguage, Partial<Record<TranslationKey, string>>> = {
  es: translations.es,
  en: translations.en,
  fr: {
    'app.language': 'Langue',
    'app.signOut': 'Sortir',
    'auth.googleLogin': 'Continuer avec Google',
    'auth.sendLink': 'Envoyer le lien',
    'dashboard.actions': 'Actions',
    'dashboard.collect': 'Collecter',
    'dashboard.stopBot': 'Arreter le bot',
    'dashboard.mobileLoginBlocker': 'Le compte est connecte sur un autre appareil. Reconnectez-vous pour continuer.',
    'intelligence.title': 'Intelligence',
    'intelligence.searchPlayer': 'Rechercher joueur ou alliance',
    'intelligence.searchPlaceholder': 'Joueur ou alliance',
    'intelligence.request': 'Demander',
    'intelligence.requesting': 'Demande...',
    'intelligence.minChars': 'Saisissez au moins 3 caracteres.',
    'intelligence.requestQueued': 'Demande envoyee',
    'intelligence.loading': 'Chargement des demandes',
    'intelligence.empty': 'Aucune demande',
    'intelligence.player': 'Joueur',
    'intelligence.search': 'Recherche',
    'intelligence.status': 'Statut',
    'intelligence.date': 'Date',
    'intelligence.result': 'Resultat',
    'intelligence.statusPending': 'En file',
    'intelligence.statusProcessing': 'Recherche',
    'intelligence.statusCompleted': 'Terminee',
    'intelligence.statusFailed': 'Resultat indisponible',
    'intelligence.statusNotFound': 'Introuvable',
    'intelligence.notFound': 'Aucune correspondance',
    'intelligence.noResult': 'Resultat en attente',
    'intelligence.power': 'Puissance',
    'intelligence.coordinates': 'Coordonnees',
    'intelligence.lastSeen': 'Vu',
    'intelligence.resultsFound': '{count} resultats',
    'intelligence.showMore': 'Afficher {count} de plus',
    'intelligence.showLess': 'Afficher moins',
    'intelligence.guarantee': 'Intelligence beta utilise des detections regionales en cache. C est une estimation, pas une position en temps reel garantie.',
    'intelligence.quota': 'Trial: 10 recherches/jour. Pro: 1 recherche/jour. Pro+: 10 recherches/jour.',
    'intelligence.fresh': 'cache recent',
    'intelligence.stale': 'cache ancien',
    'intelligence.confidence': 'Confiance',
    'intelligence.quotaRemaining': 'Recherches restantes aujourd hui',
    'intelligence.quotaRemainingValue': '{remaining} sur {limit}',
    'intelligence.quotaKicker': 'Limite quotidienne atteinte',
    'intelligence.quotaTitle': 'Vous n avez plus de recherches Intelligence aujourd hui',
    'intelligence.quotaBody': 'Votre quota se reinitialise chaque jour en UTC. Revenez demain ou consultez un plan avec plus de recherches quotidiennes.',
    'intelligence.quotaTrialLabel': 'Trial',
    'intelligence.quotaTrialValue': '10 recherches/jour',
    'intelligence.quotaProLabel': 'Pro',
    'intelligence.quotaProValue': '1 recherche/jour',
    'intelligence.quotaProPlusLabel': 'Pro+',
    'intelligence.quotaProPlusValue': '10 recherches/jour',
    'intelligence.quotaUpgrade': 'Voir l upgrade',
    'intelligence.quotaClose': 'Fermer',
    'intelligence.delete': 'Supprimer la recherche',
    'intelligence.deleteConfirm': 'Supprimer "{name}" du panneau ? Cette recherche comptera toujours dans votre limite quotidienne.',
    'intelligence.deleteSuccess': 'Recherche Intelligence retiree du panneau'
  },
  de: {
    'app.language': 'Sprache',
    'app.signOut': 'Abmelden',
    'auth.googleLogin': 'Mit Google fortfahren',
    'auth.sendLink': 'Link senden',
    'dashboard.actions': 'Aktionen',
    'dashboard.collect': 'Sammeln',
    'dashboard.stopBot': 'Bot stoppen'
  },
  pt: {
    'app.language': 'Idioma',
    'app.signOut': 'Sair',
    'auth.googleLogin': 'Entrar com Google',
    'auth.sendLink': 'Enviar link',
    'dashboard.actions': 'Acoes',
    'dashboard.collect': 'Coletar',
    'dashboard.stopBot': 'Parar bot',
    'dashboard.mobileLoginBlocker': 'A conta iniciou sessao em outro dispositivo. Reconecte para continuar.',
    'intelligence.title': 'Intelligence',
    'intelligence.searchPlayer': 'Buscar jogador ou alianca',
    'intelligence.searchPlaceholder': 'Jogador ou alianca',
    'intelligence.request': 'Solicitar',
    'intelligence.requesting': 'Solicitando...',
    'intelligence.minChars': 'Digite pelo menos 3 caracteres.',
    'intelligence.requestQueued': 'Solicitacao enviada',
    'intelligence.loading': 'Carregando solicitacoes',
    'intelligence.empty': 'Nenhuma solicitacao ainda',
    'intelligence.player': 'Jogador',
    'intelligence.search': 'Busca',
    'intelligence.status': 'Status',
    'intelligence.date': 'Data',
    'intelligence.result': 'Resultado',
    'intelligence.statusPending': 'Na fila',
    'intelligence.statusProcessing': 'Buscando',
    'intelligence.statusCompleted': 'Concluida',
    'intelligence.statusFailed': 'Nao foi possivel obter o resultado',
    'intelligence.statusNotFound': 'Nao encontrado',
    'intelligence.notFound': 'Nenhuma correspondencia',
    'intelligence.noResult': 'Resultado pendente',
    'intelligence.power': 'Poder',
    'intelligence.coordinates': 'Coordenadas',
    'intelligence.lastSeen': 'Visto',
    'intelligence.resultsFound': '{count} resultados',
    'intelligence.showMore': 'Ver mais {count}',
    'intelligence.showLess': 'Ver menos',
    'intelligence.guarantee': 'Intelligence beta usa deteccoes regionais em cache. E uma estimativa, nao uma localizacao em tempo real garantida.',
    'intelligence.quota': 'Trial: 10 buscas/dia. Pro: 1 busca/dia. Pro+: 10 buscas/dia.',
    'intelligence.fresh': 'cache recente',
    'intelligence.stale': 'cache antigo',
    'intelligence.confidence': 'Confianca',
    'intelligence.quotaRemaining': 'Buscas restantes hoje',
    'intelligence.quotaRemainingValue': '{remaining} de {limit}',
    'intelligence.quotaKicker': 'Limite diario atingido',
    'intelligence.quotaTitle': 'Voce nao tem mais buscas de Intelligence hoje',
    'intelligence.quotaBody': 'Sua cota reinicia todos os dias em UTC. Volte amanha ou revise um plano com mais buscas diarias.',
    'intelligence.quotaTrialLabel': 'Trial',
    'intelligence.quotaTrialValue': '10 buscas/dia',
    'intelligence.quotaProLabel': 'Pro',
    'intelligence.quotaProValue': '1 busca/dia',
    'intelligence.quotaProPlusLabel': 'Pro+',
    'intelligence.quotaProPlusValue': '10 buscas/dia',
    'intelligence.quotaUpgrade': 'Ver upgrade',
    'intelligence.quotaClose': 'Fechar',
    'intelligence.delete': 'Excluir busca',
    'intelligence.deleteConfirm': 'Excluir "{name}" do painel? Esta busca continuara contando no seu limite diario.',
    'intelligence.deleteSuccess': 'Busca de Intelligence removida do painel'
  }
};

interface I18nContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function normalizeLanguage(value?: string | null): AppLanguage {
  const normalized = String(value || '').toLowerCase().slice(0, 2);
  if (normalized === 'en' || normalized === 'fr' || normalized === 'de' || normalized === 'pt') return normalized;
  return 'es';
}

function initialLanguage(): AppLanguage {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) return normalizeLanguage(saved);
  } catch {
    // Ignore storage errors; language falls back to browser/default.
  }
  return normalizeLanguage(navigator.language);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(initialLanguage);

  const value = useMemo<I18nContextValue>(() => {
    const setLanguage = (next: AppLanguage) => {
      setLanguageState(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Non-critical. The selector still works for the current session.
      }
    };
    const t = (key: TranslationKey) => fallbackByLanguage[language][key] || translations.en[key] || translations.es[key] || key;
    return { language, setLanguage, t };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider');
  return value;
}
